import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate } from 'react-router-dom'

import type { Uloga } from '../api/types'
import { odjavi } from '../auth/session'
import { useSession } from '../auth/useSession'
import { Button } from '../components/Button'
import { pocetnaZaUlogu } from './rute'

/**
 * Brana pred sadržajem konzole. Redoslijed provjera nije proizvoljan:
 *
 * 1. nema sesije → prijava;
 * 2. stoji obveza promjene lozinke → promjena lozinke i ništa drugo (isto što
 *    middleware radi na poslužitelju, API §1 — ekran samo ne šalje korisnika u
 *    zid od 403-ica);
 * 3. kriva uloga → njegova vlastita početna, ne 403 stranica: operater platforme
 *    u `/admin/*` ne bi ionako vidio nijedan redak (CLAUDE.md §4).
 *
 * `uloga` smije biti i POPIS uloga, i to nije omekšavanje pravila "viša uloga ne
 * nasljeđuje nižu" (§0) nego imenovana publika: `/reports/*` gleda direktor I
 * administrator resorta, kao što `/hubs/ops` sluša recepcija I administrator.
 * Nijedno time ne dobiva prava onog drugog — a granicu ionako drži poslužitelj
 * politikom, ovo je samo routing.
 */
export function Zasticeno({
  uloga,
  children,
}: {
  uloga: Uloga | readonly Uloga[]
  children: ReactNode
}) {
  const sesija = useSession()

  if (!sesija.accessToken) {
    return <Navigate to="/login" replace />
  }

  if (sesija.moraPromijenitiLozinku) {
    return <Navigate to="/change-password" replace />
  }

  const dopustene = Array.isArray(uloga) ? uloga : [uloga as Uloga]

  if (!sesija.uloga || !dopustene.includes(sesija.uloga)) {
    return <Navigate to={pocetnaZaUlogu(sesija.uloga)} replace />
  }

  return <>{children}</>
}

/**
 * Račun kojem ne pripada nijedno sučelje (nepoznata ili izostala uloga u
 * tokenu). Poštenije je to reći nego ga pustiti u panel u kojem mu poslužitelj
 * zatvara svaki poziv.
 */
export function NedostupnoSucelje() {
  const { t } = useTranslation()
  const sesija = useSession()

  if (!sesija.accessToken) return <Navigate to="/login" replace />

  return (
    <main className="flex min-h-dvh items-center justify-center bg-noir px-4">
      <div className="w-full max-w-sm rounded-2xl border border-line bg-surface p-6 text-center">
        <h1 className="text-2xl text-ivory">{t('auth.unsupported.title')}</h1>
        <p className="mt-2 text-sm text-stone">{t('auth.unsupported.body')}</p>
        <Button izgled="gold" className="mt-6 w-full" onClick={() => void odjavi()}>
          {t('nav.logout')}
        </Button>
      </div>
    </main>
  )
}
